import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Pause,
  Play,
  PlayCircle,
  RefreshCw,
  RotateCw,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { ApiError } from "@/api/client";
import {
  listSyncJobs,
  pauseSyncJob,
  resumeSyncJob,
  startInitialSync,
  type SyncJob,
} from "@/api/sync";
import { EmptyState } from "@/components/EmptyState";
import { Spinner } from "@/components/Spinner";
import { formatDateTime } from "@/lib/formatDate";
import { AdminHeader } from "@/pages/admin/components/AdminHeader";

const AUTO_REFRESH_MS = 3000;

export function TabSyncPage() {
  const { id: tabId } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ["sync-jobs", tabId],
    queryFn: () => listSyncJobs(tabId!),
    enabled: !!tabId,
    refetchInterval: (data) => {
      const items = data.state.data?.items ?? [];
      const hasActive = items.some(
        (j) =>
          j.status === "pending" || j.status === "running" || j.pause_requested,
      );
      return hasActive ? AUTO_REFRESH_MS : false;
    },
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: ["sync-jobs", tabId] });

  const start = useMutation({
    mutationFn: () => startInitialSync(tabId!),
    onSuccess: () => {
      toast.success("Initial sync started.");
      refresh();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to start sync."),
  });

  const pause = useMutation({
    mutationFn: (jobId: number) => pauseSyncJob(tabId!, jobId),
    onSuccess: () => {
      toast.success("Pause requested. The worker will stop after the current batch.");
      refresh();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to pause."),
  });

  const resume = useMutation({
    mutationFn: (jobId: number) => resumeSyncJob(tabId!, jobId),
    onSuccess: () => {
      toast.success("Sync resumed. It picks up from the last processed grievance.");
      refresh();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to resume."),
  });

  const items = list.data?.items ?? [];
  const lastInitial = items.find((j) => j.kind === "initial_sync");
  const lastInitialFailed = lastInitial?.status === "failed";
  const lastInitialPaused = lastInitial?.status === "paused";
  const hasActiveInitial =
    lastInitial?.status === "pending" || lastInitial?.status === "running";
  const startDisabled =
    start.isPending || hasActiveInitial || lastInitialPaused;
  const startDisabledReason = hasActiveInitial
    ? "A sync is already running for this tab."
    : lastInitialPaused
    ? "There's a paused sync – resume it instead of starting a new one."
    : undefined;

  return (
    <div className="min-h-screen flex flex-col">
      <AdminHeader title="Vector sync for this tab" backTo="/admin/tabs" />
      <main className="w-full max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1 flex flex-col gap-4">
        <div className="alert-info text-xs">
          The initial sync scans every replied grievance on this tab's govt DB, embeds it, and
          seeds the tab's vector collection. It's throttled to avoid overloading the shared SQL
          Server – a 17k-ticket exam typically takes 60–90 minutes. You can pause and resume it
          any time; already-embedded points are never rewritten as duplicates (GrievanceID is
          the Qdrant point ID, so upserts replace in place).
        </div>

        {lastInitialFailed && (
          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/60 p-3 text-sm flex items-start gap-3">
            <AlertTriangle
              size={18}
              className="mt-0.5 text-amber-600 dark:text-amber-400 shrink-0"
            />
            <div className="flex-1 min-w-0 space-y-1">
              <p className="font-semibold text-amber-900 dark:text-amber-200">
                Last initial sync did not finish
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-300/90">
                {lastInitial?.error?.split("\n")[0] ||
                  "The job was interrupted before it could complete."}{" "}
                Retriggering resumes from the last processed grievance –
                no vectors are deleted or duplicated.
              </p>
            </div>
            <button
              type="button"
              className="btn-primary text-xs shrink-0 inline-flex items-center gap-1.5"
              onClick={() => start.mutate()}
              disabled={start.isPending}
              title="Start a new initial-sync job; it resumes from where the last one left off."
            >
              <RotateCw size={12} />
              {start.isPending ? "Retriggering…" : "Retrigger sync"}
            </button>
          </div>
        )}

        {lastInitialPaused && lastInitial && (
          <div className="rounded-md border border-sky-300 bg-sky-50 dark:bg-sky-950/30 dark:border-sky-800/60 p-3 text-sm flex items-start gap-3">
            <Pause
              size={18}
              className="mt-0.5 text-sky-600 dark:text-sky-400 shrink-0"
            />
            <div className="flex-1 min-w-0 space-y-1">
              <p className="font-semibold text-sky-900 dark:text-sky-200">
                Sync paused at {lastInitial.progress_pct}%
              </p>
              <p className="text-xs text-sky-800 dark:text-sky-300/90">
                Cursor preserved. Resuming continues from the last processed
                grievance.
              </p>
            </div>
            <button
              type="button"
              className="btn-primary text-xs shrink-0 inline-flex items-center gap-1.5"
              onClick={() => resume.mutate(lastInitial.id)}
              disabled={resume.isPending}
            >
              <Play size={12} />
              {resume.isPending ? "Resuming…" : "Resume sync"}
            </button>
          </div>
        )}

        <div className="card p-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">Kick off initial sync</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Idempotent – replies already embedded via Path A are skipped.
            </p>
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => start.mutate()}
            disabled={startDisabled}
            title={startDisabledReason}
          >
            {start.isPending ? <Spinner label="Starting…" /> : <><PlayCircle size={14} /> Start</>}
          </button>
        </div>

        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Job history</h2>
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => list.refetch()}
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        {list.isLoading ? (
          <div className="card p-12 flex justify-center">
            <Spinner label="Loading jobs…" />
          </div>
        ) : list.isError ? (
          <div className="alert-danger">{(list.error as Error)?.message}</div>
        ) : list.data && list.data.items.length > 0 ? (
          <ul className="card divide-y divide-canvas-border dark:divide-surface-border">
            {list.data.items.map((j) => (
              <JobRow
                key={j.id}
                job={j}
                onPause={() => pause.mutate(j.id)}
                onResume={() => resume.mutate(j.id)}
                pauseBusy={pause.isPending}
                resumeBusy={resume.isPending}
              />
            ))}
          </ul>
        ) : (
          <EmptyState
            title="No sync jobs yet."
            description="Trigger an initial sync to build this tab's vector collection from its govt DB."
          />
        )}
      </main>
    </div>
  );
}

function JobRow({
  job,
  onPause,
  onResume,
  pauseBusy,
  resumeBusy,
}: {
  job: SyncJob;
  onPause: () => void;
  onResume: () => void;
  pauseBusy: boolean;
  resumeBusy: boolean;
}) {
  const statusClass = {
    pending: "bg-slate-200 text-slate-700 dark:bg-surface-border dark:text-slate-200",
    running: "bg-brand-green/20 text-status-ackFg dark:bg-brand-green/20 dark:text-brand-green",
    paused: "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300",
    done: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
    failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  }[job.status];

  const canPause = job.status === "running" && !job.pause_requested;
  const canResume = job.status === "paused" && job.kind === "initial_sync";
  const pausePending = job.pause_requested && job.status === "running";

  return (
    <li className="px-4 py-3 space-y-1">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="font-medium">#{job.id} · {job.kind}</span>
        <div className="flex items-center gap-2">
          {pausePending && (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              pause requested…
            </span>
          )}
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusClass}`}
          >
            {job.status}
          </span>
          {canPause && (
            <button
              type="button"
              className="btn-secondary text-xs inline-flex items-center gap-1"
              onClick={onPause}
              disabled={pauseBusy}
              title="Pause after the current batch. Cursor is preserved."
            >
              <Pause size={11} /> Pause
            </button>
          )}
          {canResume && (
            <button
              type="button"
              className="btn-primary text-xs inline-flex items-center gap-1"
              onClick={onResume}
              disabled={resumeBusy}
            >
              <Play size={11} /> Resume
            </button>
          )}
        </div>
      </div>
      {(job.status === "running" || job.status === "paused") && (
        <div className="w-full bg-slate-200 dark:bg-surface-border rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all ${
              job.status === "paused" ? "bg-sky-500" : "bg-brand-green"
            }`}
            style={{ width: `${job.progress_pct}%` }}
          />
        </div>
      )}
      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
        <span>
          Created {formatDateTime(job.created_at)}
          {job.paused_at && !job.finished_at
            ? ` · paused ${formatDateTime(job.paused_at)}`
            : ""}
          {job.finished_at ? ` · finished ${formatDateTime(job.finished_at)}` : ""}
        </span>
        <span className="font-mono">{job.progress_pct}%</span>
      </div>
      {job.error && <div className="alert-danger text-xs">{job.error}</div>}
    </li>
  );
}
